
import Vue, { PropOptions } from 'vue';
import _ from 'lodash';
import moment from 'moment';
import { numFormat } from '@/helpers/numFormat';
import { SalesTarget } from '@/models/page-models/page-items/SalesTarget';

export default Vue.extend({
  props: {
    input: { type: Object, default: () => {} }  as PropOptions<SalesTarget>,
    data: { type: Object, default: () => {} },
    getVariable: { type: Function, default: () => () => {} },
    callFunction: { type: Function, default: () => () => {} },
    onChange: { type: Function, default: () => {} },
    parent: { type: Object, default: () => {} },
    rowDisabled: { type: Boolean, default: false }
  },
  data () {
    return { salesGroupExpanded: '', salesmanExpanded: '', customerExpanded: '' };
  },
  computed: {
    showCalloffVersion: function(): boolean {
      if(this.input.formVariable) {
        const { year, month } = this.getVariable(this.input.formVariable);
        const fullDate = moment(`${year} ${month}`, 'YYYY MMMM');
        const cutoffDate = moment('2025-10-01', 'YYYY-MM-DD');
        return fullDate.isBefore(cutoffDate) ? false : true;
      }
      return false;
    },
    weightedAverageKey: function(): string {
      return this.showCalloffVersion ? 'totalTargetValue' : 'targetSAR';
    },
    tableHeaders: function(): { text: string, value: string, align?: string }[] {
      const tableHeaders: { text: string, value: string, align?: string }[] = [
        { text: 'Group', value: 'salesGroup', align: 'center' },
        { text: 'Salesman', value: 'salesman', align: 'center' },
        { text: 'Customer', value: 'customer', align: 'center' },
        this.showCalloffVersion ? { text: 'Product #', value: 'productNo' } : { text: 'Order #', value: 'orderNo' },
        { text: 'Item Alias', value: 'product', align: 'center' },
        { text: 'Base Unit', value: 'baseUnit', align: 'center' },
        { text: 'Target', value: (this.showCalloffVersion ? 'totalTargetValue': 'targetSAR') },
        { text: '% Completion', value: 'completion', align: 'center' },
        { text: 'Balance', value: 'balanceSR', align: 'center' },
        { text: 'Invoiced', value: 'invoiceSum', align: 'center' },
        { text: 'Hold', value: 'hold', align: 'center' },
        { text: '', value: 'data-expand' }
      ];
      return tableHeaders;
    },
    monthlyTargetData: function(): any {
      return this.getVariable(this.input.monthlyTargetDataVariable);
    },
    groupedTargetData: function(): any[] {
      const salesTargetDataGroupedBySalesGroup = _.map(this.monthlyTargetData, ((salesGroupTargetData: any, salesGroup: string ) => {
        const salesTargetDataGroupedBySalesman = _.map(salesGroupTargetData, ((salesmanTargetData: any, salesman: string) => {
          const salesTargetDataGroupedByCustomer = _.map(salesmanTargetData, ((customerOrderDataArray: any[], customer: string) => {
            const targetSAR = customerOrderDataArray.reduce((targetSAR, orderData) => targetSAR + (orderData.targetSAR || 0), 0);
            const totalTargetValue = customerOrderDataArray.reduce((totalTargetValue, orderData) => totalTargetValue + (orderData.totalTargetValue || 0), 0);
            const balanceSR = customerOrderDataArray.reduce((balanceSR, orderData) => balanceSR + (orderData.balanceSR || 0), 0);
            const completion =  this.weightedAverage(customerOrderDataArray, this.weightedAverageKey);
            const hold = customerOrderDataArray.reduce((hold, orderData) => hold + (orderData.hold || 0), 0);
            const invoiceSum = customerOrderDataArray.reduce((invoiceSum, orderData) => invoiceSum + (orderData.invoiceSum || 0), 0);
            const customerSalesTargetArray = customerOrderDataArray.map((customerData: any) => {
              if(customerData.productLine == 'Discount Credit Note') {
                return {
                  ...customerData,
                  salesGroup,
                  product: 'Discount Credit Note',
                  productNo: '-',
                  orderNo: '-',
                  baseUnit: 'SAR',
                  targetSAR: 0,
                  totalTargetValue: 0,
                  balanceSR: 0,
                  completion: 0,
                  hold: 0
                }
              } else {
                return {...customerData, salesGroup };
              }
            });
            return {
              salesGroup,
              salesman,
              customer,
              productNo: '-',
              orderNo: '-',
              baseUnit: 'SAR',
              product: '-',
              targetSAR,
              totalTargetValue,
              balanceSR,
              completion,
              hold,
              invoiceSum,
              customerSalesTargetArray,
              expandable: 'customer'
            };
          }));
          const targetSAR = salesTargetDataGroupedByCustomer.reduce((targetSAR, orderData) => targetSAR + orderData.targetSAR, 0);
          const totalTargetValue = salesTargetDataGroupedByCustomer.reduce((totalTargetValue, orderData) => totalTargetValue + orderData.totalTargetValue, 0);
          const balanceSR = salesTargetDataGroupedByCustomer.reduce((balanceSR, orderData) => balanceSR + orderData.balanceSR, 0);
          const completion =  this.weightedAverage(salesTargetDataGroupedByCustomer, this.weightedAverageKey);
          const hold = salesTargetDataGroupedByCustomer.reduce((hold, orderData) => hold + orderData.hold, 0);
          const invoiceSum = salesTargetDataGroupedByCustomer.reduce((invoiceSum, orderData) => invoiceSum + orderData.invoiceSum, 0);
          return {
            salesGroup,
            salesman,
            customer: 'All',
            productNo: '-',
            orderNo: '-',
            baseUnit: 'SAR',
            product: '-',
            targetSAR,
            totalTargetValue,
            balanceSR,
            completion,
            hold,
            invoiceSum,
            salesTargetDataGroupedByCustomer,
            expandable: 'salesman'
          };
        }));

        const targetSAR = salesTargetDataGroupedBySalesman.reduce((targetSAR, orderData) => targetSAR + orderData.targetSAR, 0);
        const totalTargetValue = salesTargetDataGroupedBySalesman.reduce((totalTargetValue, orderData) => totalTargetValue + orderData.totalTargetValue, 0);
        const balanceSR = salesTargetDataGroupedBySalesman.reduce((balanceSR, orderData) => balanceSR + orderData.balanceSR, 0);
        const completion =  this.weightedAverage(salesTargetDataGroupedBySalesman, this.weightedAverageKey);
        const hold = salesTargetDataGroupedBySalesman.reduce((hold, orderData) => hold + orderData.hold, 0);
        const invoiceSum = salesTargetDataGroupedBySalesman.reduce((invoiceSum, orderData) => invoiceSum + orderData.invoiceSum, 0);
        return {
          salesGroup,
          salesman: 'All',
          customer: 'All',
          productNo: '-',
          orderNo: '-',
          baseUnit: 'SAR',
          product: '-',
          targetSAR,
          totalTargetValue,
          balanceSR,
          completion,
          hold,
          invoiceSum,
          salesTargetDataGroupedBySalesman,
          expandable: 'salesGroup'
        };
      }));
      const targetSAR = salesTargetDataGroupedBySalesGroup.reduce((targetSAR, orderData) => targetSAR + orderData.targetSAR, 0);
      const totalTargetValue = salesTargetDataGroupedBySalesGroup.reduce((totalTargetValue, orderData) => totalTargetValue + orderData.totalTargetValue, 0);
      const balanceSR = salesTargetDataGroupedBySalesGroup.reduce((balanceSR, orderData) => balanceSR + orderData.balanceSR, 0);
      const completion =  this.weightedAverage(salesTargetDataGroupedBySalesGroup, this.weightedAverageKey);
      const hold = salesTargetDataGroupedBySalesGroup.reduce((hold, orderData) => hold + orderData.hold, 0);
      const invoiceSum = salesTargetDataGroupedBySalesGroup.reduce((invoiceSum, orderData) => invoiceSum + orderData.invoiceSum, 0);
      const totalTargetData: any[] = [
        {
          salesGroup: 'Total Target',
          salesman: 'All',
          customer: 'All',
          productNo: '-',
          orderNo: '-',
          baseUnit: 'SAR',
          product: '-',
          targetSAR,
          totalTargetValue,
          balanceSR,
          completion,
          hold,
          invoiceSum
        }
      ];
      _.sortBy(salesTargetDataGroupedBySalesGroup, 'salesGroup').forEach((salesGroupTargetData: any) => {
        const { salesTargetDataGroupedBySalesman, ...salesGroupTargetSummary } = salesGroupTargetData;
        totalTargetData.push(salesGroupTargetSummary);
        if(salesGroupTargetSummary.salesGroup == this.salesGroupExpanded) {
          salesTargetDataGroupedBySalesman.forEach((salesmanTargetData: any) => {
            const { salesTargetDataGroupedByCustomer, ...salesmanTargetSummary } = salesmanTargetData;
            totalTargetData.push(salesmanTargetSummary);
            if(salesmanTargetSummary.salesman == this.salesmanExpanded) {
              const matchedCustomerIndex = _.findIndex(salesTargetDataGroupedByCustomer, (groupedCustomer: any) => groupedCustomer.customer == this.customerExpanded);
              if(matchedCustomerIndex == -1) {
                totalTargetData.push(...salesTargetDataGroupedByCustomer);
              } else {
                totalTargetData.push(..._.slice(salesTargetDataGroupedByCustomer, 0, matchedCustomerIndex + 1 ));
                totalTargetData.push(...salesTargetDataGroupedByCustomer[matchedCustomerIndex].customerSalesTargetArray);
                totalTargetData.push(..._.slice(salesTargetDataGroupedByCustomer, matchedCustomerIndex + 1 ));
              }
            }
          });
        }
        
      });
      return totalTargetData;
    }
  },
  mounted() {
  },
  methods: {
    expand(expandableType: 'salesGroup' | 'salesman' | 'customer', expandValue: string) {
      if(expandableType == 'salesGroup') {
        this.salesGroupExpanded = expandValue;
      } else if(expandableType == 'salesman') {
        this.salesmanExpanded = expandValue;
      } else {
        this.customerExpanded = expandValue;
      }
    },
    collapse(expandableType: 'salesGroup' | 'salesman' | 'customer') {
      if(expandableType == 'salesGroup') {
        this.salesGroupExpanded = '';
      }
      if(['salesGroup', 'salesman'].includes(expandableType)) {
        this.salesmanExpanded = '';
      }
      this.customerExpanded = '';
    },
    formatNumber(numValue: number): string | number {
      return numFormat(numValue);
    },
    weightedAverage(orderArray: any[], weightKey: string, valueKey: string = 'completion'): number {
      const weightedSumProduct = orderArray.reduce((sum, orderData) => sum + _.get(orderData, weightKey, 0) * _.get(orderData, valueKey, 0), 0);
      const weightedSum = orderArray.reduce((weight, orderData) => weight + _.get(orderData, weightKey, 0), 0);
      return weightedSum == 0 ? 0 : weightedSumProduct / weightedSum;
    }
  }
});
